import React from 'react';
import PropTypes from 'prop-types';
import Input from './Input';

/**
 * FormField
 * 
 * A standardized form field component with label, validation, help text, and character counting.
 * Provides consistent styling, accessibility, and error handling across all forms.
 */
const FormField = ({
  id,
  label,
  required = false,
  type = 'text',
  rows = 4,
  value,
  onChange,
  maxLength,
  placeholder,
  error,
  helpText,
  showCharacterCount = true,
  variant = 'default',
  className = '',
  disabled = false,
  ...rest
}) => {
  const currentLength = value?.length || 0;
  const isOverLimit = maxLength && currentLength > maxLength;
  const showCounter = showCharacterCount && maxLength;
  const isTextarea = type === 'textarea';

  // Determine input state styling
  const getInputClassName = () => {
    const baseClasses = 'block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 transition-all duration-200';
    const focusClasses = 'focus:ring-succession-primary focus:border-succession-primary';
    const resizeClass = isTextarea ? 'resize-none' : '';
    
    let stateClasses = '';
    if (error) {
      stateClasses = 'border-red-300 text-red-900 placeholder-red-300';
    } else if (isOverLimit) {
      stateClasses = 'border-yellow-300';
    } else {
      stateClasses = 'border-succession-border hover:border-succession-primary';
    }

    return `${baseClasses} ${focusClasses} ${stateClasses} ${resizeClass} ${className}`;
  };

  const inputProps = {
    id,
    value: value || '',
    onChange,
    maxLength,
    placeholder,
    disabled,
    className: getInputClassName(),
    'aria-invalid': !!error,
    'aria-describedby': `${id}-help ${error ? `${id}-error` : ''}`.trim(),
    ...rest,
  };

  return (
    <div className={variant === 'compact' ? 'space-y-1' : ''}>
      {/* Label */}
      <label 
        htmlFor={id} 
        className="block text-sm font-medium text-succession-text-secondary mb-2"
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* Input/Textarea */}
      <div className="relative">
        {isTextarea ? (
          <textarea
            rows={rows}
            {...inputProps}
          />
        ) : (
          <Input
            type={type}
            {...inputProps}
            variant={error ? 'danger' : 'primary'}
            className={className}
          />
        )}
      </div>

      {/* Error, Help Text, and Character Count */}
      <div className="mt-1 flex justify-between">
        <div className="flex-1">
          {/* Error Message */}
          {error && (
            <p id={`${id}-error`} className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          
          {/* Help Text */}
          {helpText && (
            <p id={`${id}-help`} className="text-xs text-succession-text-muted mt-1">
              {helpText}
            </p>
          )}
        </div>

        {/* Character Count */}
        {showCounter && (
          <p 
            className={`text-xs flex-shrink-0 ml-2 ${
              isOverLimit ? 'text-red-500 font-medium' : 'text-succession-text-muted'
            }`}
            aria-live="polite"
            aria-atomic="true"
          >
            {currentLength}/{maxLength}
          </p>
        )}
      </div>
    </div>
  );
};

FormField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  required: PropTypes.bool,
  type: PropTypes.oneOf(['text', 'textarea', 'email', 'number', 'tel', 'url', 'password']),
  rows: PropTypes.number,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  maxLength: PropTypes.number,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  helpText: PropTypes.node,
  showCharacterCount: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'compact']),
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

export default FormField;

